import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  FilterTypesEnum,
  ModeOptionsEnum,
  ScheduleDeliveryOptionsEnum,
  DeliveryWindowTrigger,
} from "pages/administration/notification-management/notificationmanagement.const";
import {
  NotificationEventTypes,
  Products,
} from "pages/administration/notification-management/notificationmanagement.const";

export const useNotificationManagementTranslations = () => {
  const { t } = useTranslation("notification-management");

  const filterTypeValueToTranslatedLabel = useCallback(
    (filterType: FilterTypesEnum) => {
      switch (filterType) {
        case FilterTypesEnum.ORIGIN:
          return t("notification-management:Origin");
        case FilterTypesEnum.DESTINATION:
          return t("notification-management:Destination");
        case FilterTypesEnum.SCHEDULED_DELIVERY:
          return t("notification-management:Scheduled Delivery");
        case FilterTypesEnum.PARTS_NUMBER:
          return t("notification-management:Part Number");
        case FilterTypesEnum.CARRIER:
          return t("notification-management:Carrier");
        case FilterTypesEnum.MILES_OUT:
          return t("notification-management:Miles Out");
        case FilterTypesEnum.MODE:
          return t("notification-management:Mode");
        default:
          return filterType;
      }
    },
    [t],
  );

  return { filterTypeValueToTranslatedLabel };
};

export const useTranslateEventType = () => {
  const { t } = useTranslation("notification-management");

  const translateEventType = useCallback(
    (eventType: NotificationEventTypes) => {
      switch (eventType) {
        case NotificationEventTypes.CARRIER_DELAY:
          return t("notification-management:Event Status - Carrier Delay");
        case NotificationEventTypes.EXCESSIVE_DWELL:
          return t("notification-management:Event Status - Excessive Dwell");
        case NotificationEventTypes.CARRIER_DELAY_CLEARED:
          return t(
            "notification-management:Event Status - Carrier Delay Cleared",
          );
        case NotificationEventTypes.ETA_CHANGE:
          return t("notification-management:ETA Update - Behind Schedule");
        case NotificationEventTypes.EARLY_ARRIVAL:
          return t("notification-management:ETA Update - Early Arrival");
        case NotificationEventTypes.OFF_ROUTE_RAIL:
          return t("notification-management:Event Status - Off Route Rail");
        default:
          return eventType;
      }
    },
    [t],
  );

  return { translateEventType };
};

export const useTranslateModeOptionType = () => {
  const { t } = useTranslation("notification-management");

  return useCallback(
    (filterType: ModeOptionsEnum) => {
      switch (filterType) {
        case ModeOptionsEnum.LTL:
          return "LTL";
        case ModeOptionsEnum.OCEAN:
          return t("notification-management:Ocean");
        case ModeOptionsEnum.PARCEL:
          return t("notification-management:Parcel");
        case ModeOptionsEnum.RAIL:
          return t("notification-management:Rail");
        case ModeOptionsEnum.TRUCK:
          return t("notification-management:Truck");
        case ModeOptionsEnum.AIR:
          return t("notification-management:Air");
        case ModeOptionsEnum.INTERMODAL:
          return t("notification-management:Intermodal");
        case ModeOptionsEnum.MULTIMODAL:
          return t("notification-management:Multimodal");
        default:
          return filterType;
      }
    },
    [t],
  );
};

export const useTranslateDeliveryWindowTrigger = () => {
  const { t } = useTranslation("notification-management");

  const translateDeliveryWindowTrigger = useCallback(
    (trigger: string) => {
      switch (trigger) {
        case DeliveryWindowTrigger.AFTER_DELIVERY_WINDOW_OPEN:
          return t("notification-management:After Delivery Window Open");
        case DeliveryWindowTrigger.AFTER_DELIVERY_WINDOW_CLOSE:
          return t("notification-management:Before Delivery Window Close");
        default:
          return trigger;
      }
    },
    [t],
  );

  return { translateDeliveryWindowTrigger };
};

export const useTranslateProductName = () => {
  const { t } = useTranslation("notification-management");

  const translateProductName = useCallback(
    (productName: string) => {
      switch (productName) {
        case Products.SHIPMENTS:
          return t("notification-management:Shipments");
        default:
          return productName;
      }
    },
    [t],
  );
  return { translateProductName };
};

export const useTranslateScheduledDeliveryOptionType = () => {
  const { t } = useTranslation("notification-management");

  return useCallback(
    (filterType: ScheduleDeliveryOptionsEnum) => {
      switch (filterType) {
        case ScheduleDeliveryOptionsEnum.TODAY:
          return t("notification-management:Today");
        case ScheduleDeliveryOptionsEnum.TOMORROW:
          return t("notification-management:Tomorrow");
        case ScheduleDeliveryOptionsEnum.THIS_WEEK:
          return t("notification-management:This Week");
        case ScheduleDeliveryOptionsEnum.THIS_MONTH:
          return t("notification-management:This Month");
        default:
          return filterType;
      }
    },
    [t],
  );
};
